import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './CursorTrail.module.css';
import {
  clamp,
  getVerticalDirection,
  lerp,
  normaliseCursorSpeed,
  randomBetween,
} from './cursorTrailMath';

export const DEFAULT_CURSOR_TRAIL_OPTIONS = Object.freeze({
  enableParticles: true,
  maxParticles: 50,
  bullishColor: '#00df9a',
  bearishColor: '#ff5c72',
  minLifetime: 400,
  maxLifetime: 700,
  minScale: 0.75,
  maxScale: 1.2,
  minOpacity: 0.5,
  maxOpacity: 0.8,
  minDrift: 4,
  maxDrift: 9,
  maxRotation: 6,
  minSpawnDistance: 6,
  maxSpawnDistance: 12,
  minSpawnInterval: 12,
  maxSpawnInterval: 32,
  maxSpawnPerFrame: 2,
  glow: 0,
  verticalDeadZone: 0.75,
  enableProximityGlow: true,
  proximityRadius: 12,
  proximityDuration: 200,
  proximitySelector: 'a, button:not(:disabled), [role="button"], [data-cursor-glow]',
});

const mediaQueryMatches = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;

  const hasFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return hasFinePointer && !prefersReducedMotion;
};

const subscribeToMediaQuery = (query, listener) => {
  if (typeof query.addEventListener === 'function') {
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }

  query.addListener(listener);
  return () => query.removeListener(listener);
};

const useCursorTrailEnabled = () => {
  const [enabled, setEnabled] = useState(mediaQueryMatches);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;

    const pointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setEnabled(pointerQuery.matches && !motionQuery.matches);
    const unsubscribePointer = subscribeToMediaQuery(pointerQuery, update);
    const unsubscribeMotion = subscribeToMediaQuery(motionQuery, update);

    update();
    return () => {
      unsubscribePointer();
      unsubscribeMotion();
    };
  }, []);

  return enabled;
};

const finiteOr = (value, fallback) => (Number.isFinite(value) ? value : fallback);

const renderParticle = (particle, progress) => {
  const easedProgress = 1 - Math.pow(1 - progress, 3);
  const opacity = particle.opacity * Math.pow(1 - progress, 1.45);
  const scale = lerp(particle.scale, particle.scale * 0.92, easedProgress);
  const rotation = particle.rotation * easedProgress;
  const y = particle.y - particle.drift * easedProgress;

  particle.node.style.opacity = String(opacity);
  particle.node.style.transform = `translate3d(${particle.x}px, ${y}px, 0) translate(-50%, -50%) rotate(${rotation}deg) scale(${scale})`;
};

/**
 * A pooled, pointer-transparent candlestick cursor trail. The component renders
 * a fixed number of glyphs once and mutates their transform/opacity directly.
 * Finished glyphs return to the pool; if every slot is active, the oldest slot
 * is recycled. React therefore never re-renders in response to pointer motion
 * and the DOM cannot grow during a long session.
 */
const CursorTrail = ({
  enableParticles = DEFAULT_CURSOR_TRAIL_OPTIONS.enableParticles,
  maxParticles = DEFAULT_CURSOR_TRAIL_OPTIONS.maxParticles,
  bullishColor = DEFAULT_CURSOR_TRAIL_OPTIONS.bullishColor,
  bearishColor = DEFAULT_CURSOR_TRAIL_OPTIONS.bearishColor,
  minLifetime = DEFAULT_CURSOR_TRAIL_OPTIONS.minLifetime,
  maxLifetime = DEFAULT_CURSOR_TRAIL_OPTIONS.maxLifetime,
  minScale = DEFAULT_CURSOR_TRAIL_OPTIONS.minScale,
  maxScale = DEFAULT_CURSOR_TRAIL_OPTIONS.maxScale,
  minOpacity = DEFAULT_CURSOR_TRAIL_OPTIONS.minOpacity,
  maxOpacity = DEFAULT_CURSOR_TRAIL_OPTIONS.maxOpacity,
  minDrift = DEFAULT_CURSOR_TRAIL_OPTIONS.minDrift,
  maxDrift = DEFAULT_CURSOR_TRAIL_OPTIONS.maxDrift,
  maxRotation = DEFAULT_CURSOR_TRAIL_OPTIONS.maxRotation,
  minSpawnDistance = DEFAULT_CURSOR_TRAIL_OPTIONS.minSpawnDistance,
  maxSpawnDistance = DEFAULT_CURSOR_TRAIL_OPTIONS.maxSpawnDistance,
  minSpawnInterval = DEFAULT_CURSOR_TRAIL_OPTIONS.minSpawnInterval,
  maxSpawnInterval = DEFAULT_CURSOR_TRAIL_OPTIONS.maxSpawnInterval,
  maxSpawnPerFrame = DEFAULT_CURSOR_TRAIL_OPTIONS.maxSpawnPerFrame,
  glow = DEFAULT_CURSOR_TRAIL_OPTIONS.glow,
  verticalDeadZone = DEFAULT_CURSOR_TRAIL_OPTIONS.verticalDeadZone,
  enableProximityGlow = DEFAULT_CURSOR_TRAIL_OPTIONS.enableProximityGlow,
  proximityRadius = DEFAULT_CURSOR_TRAIL_OPTIONS.proximityRadius,
  proximityDuration = DEFAULT_CURSOR_TRAIL_OPTIONS.proximityDuration,
  proximitySelector = DEFAULT_CURSOR_TRAIL_OPTIONS.proximitySelector,
}) => {
  const enabled = useCursorTrailEnabled();
  const layerRef = useRef(null);
  const particleNodesRef = useRef([]);
  const proximityGlowRef = useRef(null);
  const poolSize = enableParticles ? clamp(Math.round(finiteOr(maxParticles, 50)), 1, 128) : 0;
  const particleIndexes = useMemo(() => Array.from({ length: poolSize }, (_, index) => index), [poolSize]);

  const setParticleRef = useCallback((index, node) => {
    particleNodesRef.current[index] = node;
  }, []);

  useEffect(() => {
    if (!enabled || !layerRef.current) return undefined;

    const nodes = particleNodesRef.current.slice(0, poolSize).filter(Boolean);
    const particles = nodes.map((node) => ({
      node,
      active: false,
      bornAt: 0,
      lifetime: 0,
      x: 0,
      y: 0,
      drift: 0,
      rotation: 0,
      scale: 1,
      opacity: 0,
    }));
    const glowNode = proximityGlowRef.current;
    const motion = {
      latestPoint: null,
      processedPoint: null,
      smoothedSpeed: 0,
      distanceRemainder: 0,
      lastSpawnAt: Number.NEGATIVE_INFINITY,
      lastDirection: 'up',
      recycleCursor: 0,
      lastProximityCheckAt: Number.NEGATIVE_INFINITY,
      proximityTarget: null,
    };
    const targetGlow = {
      active: false,
      startedAt: 0,
    };
    let animationFrame = 0;

    const lifetimeLow = Math.max(100, Math.min(finiteOr(minLifetime, 400), finiteOr(maxLifetime, 700)));
    const lifetimeHigh = Math.max(lifetimeLow, Math.max(finiteOr(minLifetime, 400), finiteOr(maxLifetime, 700)));
    const scaleLow = Math.max(0.2, Math.min(finiteOr(minScale, 0.75), finiteOr(maxScale, 1.2)));
    const scaleHigh = Math.max(scaleLow, Math.max(finiteOr(minScale, 0.75), finiteOr(maxScale, 1.2)));
    const opacityLow = clamp(Math.min(finiteOr(minOpacity, 0.3), finiteOr(maxOpacity, 0.48)), 0.05, 0.9);
    const opacityHigh = clamp(Math.max(finiteOr(minOpacity, 0.3), finiteOr(maxOpacity, 0.48)), opacityLow, 0.9);
    const driftLow = Math.max(0, Math.min(finiteOr(minDrift, 4), finiteOr(maxDrift, 9)));
    const driftHigh = Math.max(driftLow, Math.max(finiteOr(minDrift, 4), finiteOr(maxDrift, 9)));
    const spawnDistanceFast = Math.max(3, finiteOr(minSpawnDistance, 6));
    const spawnDistanceSlow = Math.max(spawnDistanceFast, finiteOr(maxSpawnDistance, 12));
    const spawnIntervalFast = Math.max(10, finiteOr(minSpawnInterval, 12));
    const spawnIntervalSlow = Math.max(spawnIntervalFast, finiteOr(maxSpawnInterval, 32));
    const spawnLimit = clamp(Math.round(finiteOr(maxSpawnPerFrame, 2)), 1, 5);
    const rotationLimit = Math.max(0, finiteOr(maxRotation, 6));
    const glowLimit = Math.max(0, finiteOr(glow, 0));
    const directionDeadZone = Math.max(0, finiteOr(verticalDeadZone, 0.75));
    const targetRadius = clamp(finiteOr(proximityRadius, 12), 0, 30);
    const targetDuration = Math.max(80, finiteOr(proximityDuration, 200));

    const clearParticles = () => {
      particles.forEach((particle) => {
        particle.active = false;
        particle.node.style.opacity = '0';
      });
      targetGlow.active = false;
      motion.proximityTarget = null;
      if (glowNode) glowNode.style.opacity = '0';
    };

    const resetMotion = () => {
      motion.latestPoint = null;
      motion.processedPoint = null;
      motion.smoothedSpeed = 0;
      motion.distanceRemainder = 0;
      motion.lastSpawnAt = Number.NEGATIVE_INFINITY;
      motion.proximityTarget = null;
    };

    const claimParticle = () => {
      for (let offset = 0; offset < particles.length; offset += 1) {
        const index = (motion.recycleCursor + offset) % particles.length;
        if (!particles[index].active) {
          motion.recycleCursor = (index + 1) % particles.length;
          return particles[index];
        }
      }

      let oldest = particles[0];
      particles.forEach((particle) => {
        if (particle.bornAt < oldest.bornAt) oldest = particle;
      });
      return oldest;
    };

    const spawnParticle = (x, y, now, speedProgress, direction) => {
      if (particles.length === 0) return;

      const particle = claimParticle();
      const baseLifetime = lerp(lifetimeLow, lifetimeHigh, speedProgress);
      const lifetimeJitter = Math.min(55, (lifetimeHigh - lifetimeLow) / 3);
      const baseScale = lerp(scaleLow, scaleHigh, speedProgress);
      const baseOpacity = lerp(opacityLow, opacityHigh, speedProgress);

      particle.active = true;
      particle.bornAt = now;
      particle.lifetime = randomBetween(
        Math.max(lifetimeLow, baseLifetime - lifetimeJitter),
        Math.min(lifetimeHigh, baseLifetime + lifetimeJitter),
      );
      particle.x = x;
      particle.y = y;
      particle.drift = randomBetween(driftLow, driftHigh);
      particle.rotation = randomBetween(-rotationLimit, rotationLimit);
      particle.scale = clamp(baseScale + randomBetween(-0.06, 0.06), scaleLow, scaleHigh);
      particle.opacity = clamp(baseOpacity * randomBetween(0.9, 1), opacityLow, opacityHigh);
      particle.node.style.color = direction === 'down' ? bearishColor : bullishColor;
      particle.node.style.setProperty('--cursor-trail-glow', `${lerp(glowLimit * 0.6, glowLimit, speedProgress)}px`);
      renderParticle(particle, 0);
    };

    const findProximityTarget = (x, y) => {
      if (!enableProximityGlow || !proximitySelector || typeof document.elementsFromPoint !== 'function') return null;

      const samples = [
        [0, 0],
        [targetRadius, 0],
        [-targetRadius, 0],
        [0, targetRadius],
        [0, -targetRadius],
      ];

      try {
        for (const [offsetX, offsetY] of samples) {
          const elements = document.elementsFromPoint(x + offsetX, y + offsetY);
          for (const element of elements) {
            const target = typeof element.closest === 'function' ? element.closest(proximitySelector) : null;
            if (target && !layerRef.current.contains(target)) return target;
          }
        }
      } catch (error) {
        // An invalid custom selector should disable only the optional glow.
        return null;
      }

      return null;
    };

    const showProximityGlow = (target, now) => {
      if (!glowNode || typeof target.getBoundingClientRect !== 'function') return;

      const rect = target.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      const padding = 2;
      const computedStyle = window.getComputedStyle(target);
      glowNode.style.width = `${rect.width + padding * 2}px`;
      glowNode.style.height = `${rect.height + padding * 2}px`;
      glowNode.style.borderRadius = computedStyle.borderRadius || '8px';
      glowNode.style.transform = `translate3d(${rect.left - padding}px, ${rect.top - padding}px, 0)`;
      glowNode.style.opacity = '1';
      targetGlow.active = true;
      targetGlow.startedAt = now;
    };

    const checkProximity = (point, now) => {
      if (!enableProximityGlow || now - motion.lastProximityCheckAt < 34) return;
      motion.lastProximityCheckAt = now;

      const target = findProximityTarget(point.x, point.y);
      if (target === motion.proximityTarget) return;
      motion.proximityTarget = target;
      if (target) showProximityGlow(target, now);
    };

    const updateProximityGlow = (now) => {
      if (!targetGlow.active || !glowNode) return false;

      const progress = clamp((now - targetGlow.startedAt) / targetDuration, 0, 1);
      glowNode.style.opacity = String(Math.pow(1 - progress, 1.7));
      if (progress >= 1) {
        targetGlow.active = false;
        glowNode.style.opacity = '0';
        return false;
      }
      return true;
    };

    const processPointerSegment = (from, to, now) => {
      const deltaX = to.x - from.x;
      const deltaY = to.y - from.y;
      const distance = Math.hypot(deltaX, deltaY);
      const elapsed = clamp(to.time - from.time, 8, 100);
      const instantaneousSpeed = (distance / elapsed) * 1000;
      motion.smoothedSpeed = lerp(motion.smoothedSpeed, instantaneousSpeed, 0.28);
      const speedProgress = normaliseCursorSpeed(motion.smoothedSpeed);
      motion.lastDirection = getVerticalDirection(deltaY, motion.lastDirection, directionDeadZone);
      motion.distanceRemainder += distance;

      const spacing = lerp(spawnDistanceSlow, spawnDistanceFast, speedProgress);
      const interval = lerp(spawnIntervalSlow, spawnIntervalFast, speedProgress);
      if (motion.distanceRemainder < spacing || now - motion.lastSpawnAt < interval) return;

      // Distance carries forward between frames. At high speed a second sample
      // may be emitted, but its animation age is staggered so it reads as part
      // of a continuous path rather than a same-age particle block.
      const count = Math.min(spawnLimit, Math.floor(motion.distanceRemainder / spacing));
      for (let index = 0; index < count; index += 1) {
        const progress = (index + 1) / (count + 1);
        const stagger = count > 1 ? Math.min(8, elapsed / count) * (count - index - 1) : 0;
        spawnParticle(
          lerp(from.x, to.x, progress),
          lerp(from.y, to.y, progress),
          now - stagger,
          speedProgress,
          motion.lastDirection,
        );
      }

      motion.distanceRemainder = Math.min(
        Math.max(0, motion.distanceRemainder - count * spacing),
        spacing * 2,
      );
      motion.lastSpawnAt = now;
    };

    const tick = (now) => {
      animationFrame = 0;
      if (document.hidden) return;

      if (motion.latestPoint) {
        if (!motion.processedPoint) {
          motion.processedPoint = motion.latestPoint;
        } else if (motion.processedPoint !== motion.latestPoint) {
          if (enableParticles) processPointerSegment(motion.processedPoint, motion.latestPoint, now);
          motion.processedPoint = motion.latestPoint;
        }
        checkProximity(motion.latestPoint, now);
      }

      let hasActiveParticles = false;
      particles.forEach((particle) => {
        if (!particle.active) return;

        const progress = (now - particle.bornAt) / particle.lifetime;
        if (progress >= 1) {
          particle.active = false;
          particle.node.style.opacity = '0';
          return;
        }

        hasActiveParticles = true;
        renderParticle(particle, clamp(progress, 0, 1));
      });

      const hasActiveGlow = updateProximityGlow(now);
      const hasPendingPoint = motion.latestPoint !== motion.processedPoint;
      if (hasActiveParticles || hasActiveGlow || hasPendingPoint) {
        animationFrame = window.requestAnimationFrame(tick);
      }
    };

    const requestTick = () => {
      if (!animationFrame && !document.hidden) animationFrame = window.requestAnimationFrame(tick);
    };

    const handlePointerMove = (event) => {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      motion.latestPoint = {
        x: event.clientX,
        y: event.clientY,
        time: performance.now(),
      };
      requestTick();
    };

    const handlePointerLeave = (event) => {
      if (event.relatedTarget) return;
      motion.proximityTarget = null;
      if (glowNode) glowNode.style.opacity = '0';
      targetGlow.active = false;
    };

    const handleViewportChange = () => {
      motion.proximityTarget = null;
      motion.processedPoint = null;
      motion.latestPoint = null;
      targetGlow.active = false;
      if (glowNode) glowNode.style.opacity = '0';
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (animationFrame) window.cancelAnimationFrame(animationFrame);
        animationFrame = 0;
        clearParticles();
      }
      resetMotion();
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('pointerout', handlePointerLeave, { passive: true });
    window.addEventListener('scroll', handleViewportChange, { passive: true, capture: true });
    window.addEventListener('blur', handleViewportChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      clearParticles();
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerout', handlePointerLeave);
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('blur', handleViewportChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    bearishColor,
    bullishColor,
    enableParticles,
    enableProximityGlow,
    enabled,
    glow,
    maxDrift,
    maxLifetime,
    maxOpacity,
    maxParticles,
    maxRotation,
    maxScale,
    maxSpawnDistance,
    maxSpawnInterval,
    maxSpawnPerFrame,
    minDrift,
    minLifetime,
    minOpacity,
    minScale,
    minSpawnDistance,
    minSpawnInterval,
    poolSize,
    proximityDuration,
    proximityRadius,
    proximitySelector,
    verticalDeadZone,
  ]);

  if (!enabled) return null;

  return (
    <div ref={layerRef} className={styles.layer} aria-hidden="true" data-testid="cursor-trail">
      {particleIndexes.map((index) => (
        <span
          key={index}
          ref={(node) => setParticleRef(index, node)}
          className={styles.particle}
          data-cursor-particle=""
        />
      ))}
      {enableProximityGlow ? (
        <span ref={proximityGlowRef} className={styles.proximityGlow} data-cursor-proximity-glow="" />
      ) : null}
    </div>
  );
};

export default CursorTrail;
